<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import countries from 'shared/constants/countries.js';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  activeFilters: {
    type: Object,
    default: () => ({ countryCode: null, city: '' }),
  },
});

const emit = defineEmits(['apply']);

const { t } = useI18n();
const dialogRef = ref(null);

const localFilters = ref({ countryCode: null, city: '' });

const countryOptions = computed(() =>
  countries.map(c => ({ value: c.id, label: c.name }))
);

watch(
  () => props.activeFilters,
  filters => {
    localFilters.value = { ...filters };
  },
  { immediate: true }
);

const clearAll = () => {
  localFilters.value = { countryCode: null, city: '' };
};

const handleApply = () => {
  emit('apply', { ...localFilters.value });
  dialogRef.value?.close();
};

const open = () => dialogRef.value?.open();

defineExpose({ open });
</script>

<template>
  <Dialog ref="dialogRef" width="md" @close="() => {}">
    <div class="flex flex-col gap-5">
      <span class="text-sm font-semibold text-n-slate-12">
        {{ t('COMPANIES.FILTER.TITLE') }}
      </span>

      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-1.5">
          <span
            class="text-xs font-medium text-n-slate-11 uppercase tracking-wide"
          >
            {{ t('COMPANIES.FILTER.COUNTRY') }}
          </span>
          <ComboBox
            v-model="localFilters.countryCode"
            :options="countryOptions"
            :placeholder="t('COMPANIES.FILTER.ALL_COUNTRIES')"
          />
        </div>

        <div class="flex flex-col gap-1.5">
          <span
            class="text-xs font-medium text-n-slate-11 uppercase tracking-wide"
          >
            {{ t('COMPANIES.FILTER.CITY') }}
          </span>
          <Input
            v-model="localFilters.city"
            :placeholder="t('COMPANIES.FILTER.CITY_PLACEHOLDER')"
            custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('COMPANIES.FILTER.CLEAR')"
          variant="link"
          type="reset"
          class="h-10 hover:!no-underline hover:text-n-ruby-9"
          @click="clearAll"
        />
        <Button
          :label="t('COMPANIES.FILTER.APPLY')"
          color="blue"
          type="submit"
          @click="handleApply"
        />
      </div>
    </template>
  </Dialog>
</template>
